<template>
  <div class="task-detail">
    <!-- Parent Breadcrumb (JIRA-style) -->
    <div class="task-detail__header" v-if="task">
      <div class="task-detail__breadcrumb" v-if="isSubtask">
        <a href="#" class="task-detail__parent-link" @click.prevent="navigateToParent">
          <theme-icon icon="chevron-left" type="icons" classes="w-3 h-3" />
          <span>{{ parentKey }} {{ parentTitle }}</span>
        </a>
        <span class="task-detail__breadcrumb-sep">/</span>
        <span class="task-detail__key">{{ task.task_key }}</span>
        <span class="task-detail__current">{{ task.title }}</span>
      </div>
      <div class="task-detail__title-row" v-else>
        <span class="task-detail__key">{{ task.task_key }}</span>
        <h2 class="task-detail__title">{{ task.title }}</h2>
      </div>
    </div>

    <!-- Edit Form Section -->
    <div class="task-detail__section">
      <task-form
        ref="taskForm"
        :task="task"
        :project-id="lockToProject ? (task ? task.project_id : null) : null"
        :site-id="lockToSite ? (task ? task.site_id : null) : null"
        @save="handleTaskSaved"
        @cancel="$emit('close')"
        @loading-change="formLoading = $event; $emit('loading-change', $event)"
      />
    </div>

    <!-- Sub-tasks Section (only for root tasks, not subtasks) -->
    <div class="task-detail__section" v-if="task && task.id && !isSubtask">
      <task-subtasks
        :task-id="task.id"
        :can-edit="canEdit"
        @open-subtask="handleOpenSubtask"
        @subtask-created="refreshTask"
      />
    </div>

    <!-- Linked Tasks Section -->
    <div class="task-detail__section" v-if="task && task.id">
      <task-links
        :task-id="task.id"
        :can-edit="canEdit"
        @link-added="refreshTask"
        @link-removed="refreshTask"
      />
    </div>

    <!-- Comments Section -->
    <div class="task-detail__section" v-if="task && task.id">
      <task-comments
        :task-id="task.id"
        @comment-added="refreshTask"
        @comment-deleted="refreshTask"
      />
    </div>
  </div>
</template>

<script>
import TaskForm from './TaskForm.vue';
import TaskSubtasks from './TaskSubtasks.vue';
import TaskLinks from './TaskLinks.vue';
import TaskComments from './TaskComments.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'TaskDetail',

  components: {
    TaskForm,
    TaskSubtasks,
    TaskLinks,
    TaskComments,
    ThemeIcon
  },

  props: {
    task: {
      type: Object,
      default: null
    },
    lockToProject: {
      type: Boolean,
      default: false
    },
    lockToSite: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formLoading: false,
      parentTask: null
    };
  },

  computed: {
    isSubtask() {
      return this.task && this.task.parent_id;
    },

    parentTitle() {
      if (this.task.parent && this.task.parent.title) {
        return this.task.parent.title;
      }
      if (this.parentTask && this.parentTask.title) {
        return this.parentTask.title;
      }
      return 'Parent Task';
    },

    parentKey() {
      if (this.task.parent && this.task.parent.task_key) {
        return this.task.parent.task_key;
      }
      if (this.parentTask && this.parentTask.task_key) {
        return this.parentTask.task_key;
      }
      return '';
    },

    canEdit() {
      if (!this.task) return false;
      const userId = window.user?.id;
      const isAdmin = window.user?.is_client_admin;
      return isAdmin || this.task.assigned_to === userId || this.task.created_by === userId;
    }
  },

  watch: {
    task: {
      immediate: true,
      handler(newTask) {
        if (newTask && newTask.parent_id && !newTask.parent) {
          this.fetchParent(newTask.parent_id);
        } else {
          this.parentTask = null;
        }
      }
    }
  },

  methods: {
    async fetchParent(parentId) {
      try {
        const response = await axios.get(`/api/tasks/${parentId}`);
        this.parentTask = response.data;
      } catch (error) {
        console.error('Error fetching parent task:', error);
      }
    },

    navigateToParent() {
      const parent = this.task.parent || this.parentTask;
      if (parent) {
        this.$emit('open-task', parent);
      }
    },

    handleTaskSaved(savedTask) {
      this.$emit('save', savedTask);
    },

    handleOpenSubtask(subtask) {
      this.$emit('open-task', subtask);
    },

    refreshTask() {
      this.$emit('refresh');
    },

    submitForm() {
      if (this.$refs.taskForm) {
        this.$refs.taskForm.handleSubmit();
      }
    }
  }
};
</script>
